import type { RecordField, RecordTimeline, RevertTarget } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { useId, useState } from 'react';
import { show, valueTitle } from '../../../components/History';
import { api } from '../../../lib/api';
import { dateTime } from '../../../lib/format';
import { Button } from '../../../ui/button';
import { Sheet } from '../../../ui/dialog';
import { Badge, ErrorBox, Spinner } from '../../../ui/feedback';
import { Field, Input } from '../../../ui/form';
import { actorName, ChangeRows, fieldTitle, OP_WORD } from './shared';

/** «схема.таблица:номер» → части; неверная ссылка — null */
export function parseRecord(
  record: string | undefined,
): { schema: string; table: string; rowId: string } | null {
  const m = /^([a-z_]+)\.([a-z_0-9]+):([0-9a-f-]{36})$/i.exec(record ?? '');
  return m ? { schema: m[1]!, table: m[2]!, rowId: m[3]!.toLowerCase() } : null;
}

/** Значение для поля «дата и время» по часам человека */
function localInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function FieldList({ table, fields }: { table: string; fields: RecordField[] }) {
  if (!fields.length) return <p className="text-[13px] text-ink-2">Полей для показа нет.</p>;
  return (
    <dl className="flex flex-col gap-1.5 rounded-md bg-surface-2 px-3 py-2.5 text-[13px]">
      {fields.map((f) => (
        <div key={f.field} className="grid grid-cols-[minmax(92px,34%)_1fr] gap-2">
          <dt className="[overflow-wrap:anywhere] text-ink-2">{fieldTitle(f.field, f.title)}</dt>
          <dd className="min-w-0 [overflow-wrap:anywhere] text-ink">
            {f.hidden ? (
              <span className="text-ink-3 italic">скрыто</span>
            ) : (
              (f.label ?? show(valueTitle(table, f.field, f.value), f.field))
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

const STATE_TEXT = {
  existed: 'Так запись выглядела в этот момент',
  absent: 'Записи в этот момент ещё не было: возврат удалит её',
  deleted: 'К этому моменту запись была удалена: возврат удалит её снова',
  unknown: 'Это раньше начала журнала — состояние неизвестно',
} as const;

/**
 * История одной записи: все изменения, состояние на любой момент и возврат к нему. Возврат отменяет действия
 * после этого момента, которые меняли запись, — целиком, вместе со связанными записями.
 */
export function RecordSheet({
  record,
  canRevert,
  onClose,
  onAction,
  onRevert,
}: {
  record: { schema: string; table: string; rowId: string };
  canRevert: boolean;
  onClose: () => void;
  onAction: (key: string) => void;
  onRevert: (target: RevertTarget, title: string, description?: string) => void;
}) {
  const atId = useId();
  const [atInput, setAtInput] = useState(() => localInput(new Date()));
  const [at, setAt] = useState<string | undefined>(undefined);
  const q = useQuery({
    queryKey: ['journal', 'record', record, at],
    queryFn: () => api<RecordTimeline>('/admin/journal/record', { query: { ...record, at } }),
    placeholderData: (prev) => prev,
  });
  const r = q.data;
  const label = r ? [r.tableTitle, r.recordLabel].filter(Boolean).join(' ') : '';
  const revertTo = (iso: string, when: string) =>
    onRevert(
      { kind: 'record', schema: record.schema, table: record.table, rowId: record.rowId, at: iso },
      'Вернуть запись',
      `${label} — как было ${when}`,
    );

  return (
    <Sheet
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="История записи"
      description={label || undefined}
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : r ? (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap gap-1.5">
            {r.exists ? (
              <Badge tone="success">запись есть</Badge>
            ) : (
              <Badge tone="danger">записи сейчас нет</Badge>
            )}
            <Badge>изменений в журнале: {r.entries.length}</Badge>
          </div>

          <section className="flex flex-col gap-3">
            <h3 className="text-[14px] font-semibold text-ink">Состояние на момент</h3>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const d = new Date(atInput);
                if (!Number.isNaN(d.getTime())) setAt(d.toISOString());
              }}
            >
              <Field label="Дата и время" htmlFor={atId} className="min-w-0 flex-1">
                <Input
                  id={atId}
                  type="datetime-local"
                  value={atInput}
                  max={localInput(new Date())}
                  onChange={(e) => setAtInput(e.target.value)}
                />
              </Field>
              <Button type="submit" loading={q.isFetching && Boolean(at)}>
                Показать
              </Button>
            </form>
            {r.stateAt ? (
              <div className="flex flex-col gap-2">
                <p className="text-[13px] text-ink-2">
                  <span className="num">{dateTime(r.stateAt.at)}</span>: {STATE_TEXT[r.stateAt.state]}
                </p>
                {r.stateAt.state === 'existed' ? (
                  <FieldList table={r.table} fields={r.stateAt.fields} />
                ) : null}
                {canRevert && r.stateAt.state !== 'unknown' ? (
                  <div>
                    <Button
                      variant="danger"
                      icon={<RotateCcw className="size-4" aria-hidden />}
                      onClick={() => revertTo(r.stateAt!.at, `на ${dateTime(r.stateAt!.at)}`)}
                    >
                      Вернуть запись к этому состоянию
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          {r.current ? (
            <details className="group">
              <summary className="inline-flex min-h-11 cursor-pointer items-center text-[14px] font-semibold text-ink md:min-h-0">
                Сейчас
              </summary>
              <div className="mt-2">
                <FieldList table={r.table} fields={r.current} />
              </div>
            </details>
          ) : null}

          <section className="flex flex-col gap-3">
            <h3 className="text-[14px] font-semibold text-ink">Изменения, новые сверху</h3>
            {!r.entries.length ? (
              <p className="text-[13px] text-ink-2">В журнале изменений этой записи нет.</p>
            ) : (
              <ol className="flex flex-col">
                {r.entries.map((e) => (
                  <li key={e.id} className="relative border-l border-line pb-5 pl-5 last:pb-0">
                    <span className="absolute top-1.5 -left-[4px] size-2 rounded-full bg-ink-3" aria-hidden />
                    <div className="text-[13px] text-ink-2">
                      <span className="num">{dateTime(e.at)}</span> · {actorName(e.actor)}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[14px] text-ink">
                      <b className="font-semibold">{OP_WORD[e.op]}</b>
                      {e.reverted ? <Badge tone="warning">отменено</Badge> : null}
                    </div>
                    <ChangeRows op={e.op} table={e.table} changes={e.changes} limit={6} />
                    <div className="mt-1 flex flex-wrap gap-x-4">
                      {canRevert && !e.reverted && e.actionKey ? (
                        <button
                          type="button"
                          onClick={() =>
                            revertTo(
                              new Date(new Date(e.at).getTime() - 1).toISOString(),
                              `до изменения ${dateTime(e.at)}`,
                            )
                          }
                          className="inline-flex min-h-11 items-center text-[13px] font-medium text-danger underline underline-offset-2 md:min-h-0"
                        >
                          Вернуть как было до этого
                        </button>
                      ) : null}
                      {e.actionKey ? (
                        <button
                          type="button"
                          onClick={() => onAction(e.actionKey!)}
                          className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline underline-offset-2 md:min-h-0"
                        >
                          Всё действие
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      ) : null}
    </Sheet>
  );
}
